import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { tomtomLabApiRef } from '../api';
import { IdeaComment } from '../types';

/**
 * Idea likes and comments, persisted through the tomtom-lab backend.
 *
 * Like state is seeded from the `likes` / `likedByMe` fields the ideas queries
 * already return, so opening the list costs no extra requests. Comments are
 * fetched per idea the first time something asks for them — the list only shows
 * counts, so bodies are only needed once a detail dialog opens.
 *
 * Mutations apply optimistically and roll back if the request fails, so the UI
 * never shows a like or comment the database rejected.
 */

type EngagementState = {
  liked: boolean;
  likeCount: number;
  comments: IdeaComment[];
  commentsLoaded: boolean;
};

const EMPTY_STATE: EngagementState = {
  liked: false,
  likeCount: 0,
  comments: [],
  commentsLoaded: false,
};

type EngagementStore = Record<number, EngagementState>;

interface IdeaEngagementContextValue {
  getEngagement: (ideaId: number) => EngagementState;
  /** Seeds counts from an idea record without clobbering local edits. */
  seedEngagement: (
    ideaId: number,
    seed: { likes?: number; likedByMe?: boolean },
  ) => void;
  loadComments: (ideaId: number) => Promise<void>;
  toggleLike: (ideaId: number) => Promise<void>;
  addComment: (ideaId: number, text: string) => Promise<void>;
  editComment: (
    ideaId: number,
    commentId: number,
    text: string,
  ) => Promise<void>;
  deleteComment: (ideaId: number, commentId: number) => Promise<void>;
}

const noop = async () => {};

const IdeaEngagementContext = createContext<IdeaEngagementContextValue>({
  getEngagement: () => EMPTY_STATE,
  seedEngagement: () => {},
  loadComments: noop,
  toggleLike: noop,
  addComment: noop,
  editComment: noop,
  deleteComment: noop,
});

export const IdeaEngagementProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const api = useApi(tomtomLabApiRef);
  const [store, setStore] = useState<EngagementStore>({});

  // Tracks in-flight comment loads so concurrent renders don't double-fetch.
  const loadingComments = useRef<Set<number>>(new Set());

  /**
   * Monotonic per-idea counter for like requests. Rapid like/unlike pairs can
   * come back out of order, so a response is only applied if no newer toggle
   * has been dispatched in the meantime — otherwise a stale reply (or a stale
   * rollback) would overwrite the newer intent.
   */
  const likeGeneration = useRef<Map<number, number>>(new Map());

  /** Ideas with a like request awaiting its response. */
  const inFlightLikes = useRef<Set<number>>(new Set());

  /**
   * Mirrors `store` so an async action can read the current value the moment it
   * needs it. React applies state updaters during render, so a rapid second
   * click cannot rely on `store` from its closure, and deriving inside an
   * updater callback is not an option either — the callback has not run yet by
   * the time the request is dispatched.
   */
  const storeRef = useRef<EngagementStore>({});

  const getEngagement = useCallback(
    (ideaId: number): EngagementState => store[ideaId] ?? EMPTY_STATE,
    [store],
  );

  const current = useCallback(
    (ideaId: number): EngagementState =>
      storeRef.current[ideaId] ?? EMPTY_STATE,
    [],
  );

  const update = useCallback(
    (ideaId: number, patch: Partial<EngagementState>) => {
      const next = {
        ...storeRef.current,
        [ideaId]: { ...(storeRef.current[ideaId] ?? EMPTY_STATE), ...patch },
      };
      storeRef.current = next;
      setStore(next);
    },
    [],
  );

  const seedEngagement = useCallback(
    (ideaId: number, seed: { likes?: number; likedByMe?: boolean }) => {
      // Apply fresh server counts on every refresh, so likes other people added
      // during the session do show up. The one thing that must not be
      // overwritten is a toggle still waiting on its own response — that would
      // revert the user's click under them.
      if (inFlightLikes.current.has(ideaId)) {
        return;
      }

      const existing = storeRef.current[ideaId];
      const liked = Boolean(seed.likedByMe);
      const likeCount = seed.likes ?? 0;
      if (
        existing &&
        existing.liked === liked &&
        existing.likeCount === likeCount
      ) {
        return;
      }

      update(ideaId, { liked, likeCount });
    },
    [update],
  );

  const loadComments = useCallback(
    async (ideaId: number) => {
      if (loadingComments.current.has(ideaId)) {
        return;
      }
      loadingComments.current.add(ideaId);
      try {
        const comments = await api.getIdeaComments(ideaId);
        update(ideaId, { comments, commentsLoaded: true });
      } finally {
        loadingComments.current.delete(ideaId);
      }
    },
    [api, update],
  );

  const toggleLike = useCallback(
    async (ideaId: number) => {
      const generation = (likeGeneration.current.get(ideaId) ?? 0) + 1;
      likeGeneration.current.set(ideaId, generation);

      const isCurrent = () => likeGeneration.current.get(ideaId) === generation;

      // Derive from the latest state, not a captured copy, so a second click
      // before the first response toggles from the right value.
      const previous = current(ideaId);
      const nextLiked = !previous.liked;

      update(ideaId, {
        liked: nextLiked,
        likeCount: Math.max(previous.likeCount + (nextLiked ? 1 : -1), 0),
      });

      inFlightLikes.current.add(ideaId);
      try {
        const result = nextLiked
          ? await api.likeIdea(ideaId)
          : await api.unlikeIdea(ideaId);

        // A newer toggle is already in flight; its response is the truth.
        if (!isCurrent()) {
          return;
        }

        update(ideaId, {
          liked: result.likedByMe,
          likeCount: result.likes,
        });
      } catch (error) {
        if (isCurrent()) {
          update(ideaId, {
            liked: previous.liked,
            likeCount: previous.likeCount,
          });
        }
        throw error;
      } finally {
        // Only the newest toggle clears the flag; an older one finishing must
        // not unblock seeding while a newer request is still out.
        if (isCurrent()) {
          inFlightLikes.current.delete(ideaId);
        }
      }
    },
    [api, current, update],
  );

  const addComment = useCallback(
    async (ideaId: number, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // The POST returns the stored row, so there is no follow-up request whose
      // failure could make a persisted comment look like it failed to save.
      const created = await api.addIdeaComment(ideaId, trimmed);

      update(ideaId, { comments: [...current(ideaId).comments, created] });
    },
    [api, current, update],
  );

  const editComment = useCallback(
    async (ideaId: number, commentId: number, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const previous = current(ideaId).comments;

      update(ideaId, {
        comments: previous.map(comment =>
          comment.id === commentId ? { ...comment, comment: trimmed } : comment,
        ),
      });

      try {
        const updated = await api.updateIdeaComment(commentId, trimmed);
        update(ideaId, {
          comments: previous.map(comment =>
            comment.id === commentId ? updated : comment,
          ),
        });
      } catch (error) {
        update(ideaId, { comments: previous });
        throw error;
      }
    },
    [api, current, update],
  );

  const deleteComment = useCallback(
    async (ideaId: number, commentId: number) => {
      const previous = current(ideaId).comments;

      update(ideaId, {
        comments: previous.filter(comment => comment.id !== commentId),
      });

      try {
        await api.deleteIdeaComment(commentId);
      } catch (error) {
        update(ideaId, { comments: previous });
        throw error;
      }
    },
    [api, current, update],
  );

  const value = useMemo(
    () => ({
      getEngagement,
      seedEngagement,
      loadComments,
      toggleLike,
      addComment,
      editComment,
      deleteComment,
    }),
    [
      getEngagement,
      seedEngagement,
      loadComments,
      toggleLike,
      addComment,
      editComment,
      deleteComment,
    ],
  );

  return (
    <IdeaEngagementContext.Provider value={value}>
      {children}
    </IdeaEngagementContext.Provider>
  );
};

/** Returns the engagement state and actions for a single idea. */
export const useIdeaEngagement = (ideaId?: number) => {
  const {
    getEngagement,
    seedEngagement,
    loadComments,
    toggleLike,
    addComment,
    editComment,
    deleteComment,
  } = useContext(IdeaEngagementContext);
  // Ideas without an id (e.g. not yet persisted) share a throwaway bucket.
  const key = ideaId ?? -1;
  const { liked, likeCount, comments, commentsLoaded } = getEngagement(key);

  return {
    liked,
    likeCount,
    comments,
    commentsLoaded,
    seed: useCallback(
      (seedValues: { likes?: number; likedByMe?: boolean }) =>
        seedEngagement(key, seedValues),
      [seedEngagement, key],
    ),
    loadComments: useCallback(() => loadComments(key), [loadComments, key]),
    toggleLike: useCallback(() => toggleLike(key), [toggleLike, key]),
    addComment: useCallback(
      (text: string) => addComment(key, text),
      [addComment, key],
    ),
    editComment: useCallback(
      (commentId: number, text: string) => editComment(key, commentId, text),
      [editComment, key],
    ),
    deleteComment: useCallback(
      (commentId: number) => deleteComment(key, commentId),
      [deleteComment, key],
    ),
  };
};
